import React, { useState, useEffect } from 'react';
import './mon-compte.css';
import { auth } from './firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, updateProfile, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import Footer from './Footer';
import { collection, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';

const Compte = ({ hidden, toggleFooter }) => {
    const [isRegistering, setIsRegistering] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [prenom, setPrenom] = useState("");
    const [nom, setNom] = useState("");
    const [telephone, setTelephone] = useState("");
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const { user, setUser } = useAuth();

    const adminEmails = ["youenhamon@gmail.com", "victorleoleblanc@gmail.com", "nicolas.tannevet@orange.fr"];

    // Ajoutez un état pour contrôler si l'inscription est en cours
    const [isRegisteringInProgress, setIsRegisteringInProgress] = useState(false);
    const [isLoggingInProgress, setIsLoggingInProgress] = useState(false);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
            // Ne pas exécuter la logique si l'inscription est en cours
            if (isRegisteringInProgress) return;

            if (firebaseUser) {
                const userDoc = await getDoc(doc(collection(db, "users"), firebaseUser.uid));
                const userData = userDoc.data();

                if (!firebaseUser.emailVerified) {
                    await signOut(auth);
                    setUser(null);
                    alert("Veuillez vérifier votre adresse e-mail pour vous connecter. Si vous n'avez pas reçu d'e-mail, il est possible que vous ayez fait une erreur dans l'adresse saisie lors de l'inscription.");
                } else {
                    setUser({
                        email: firebaseUser.email,
                        prenom: userData?.prenom || "Utilisateur",
                        isAdmin: userData?.isAdmin || false,
                    });
                }
            } else {
                setUser(null);
            }
        });

        return () => unsubscribe();
    }, [setUser, isRegisteringInProgress]);

    // Dictionnaire d'erreurs Firebase avec messages compréhensibles
    const firebaseErrorMessages = {
        // Email & mot de passe
        "auth/email-already-in-use": "Cette adresse e-mail est déjà utilisée.",
        "auth/invalid-email": "L'adresse e-mail est invalide.",
        "auth/user-disabled": "Ce compte a été désactivé.",
        "auth/user-not-found": "Aucun compte trouvé avec cette adresse e-mail.",
        "auth/wrong-password": "Mot de passe incorrect.",
        "auth/weak-password": "Le mot de passe est trop faible (6 caractères minimum).",

        // Comptes et identifiants
        "auth/credential-already-in-use": "Cette méthode d’authentification est déjà utilisée par un autre compte.",
        "auth/account-exists-with-different-credential": "Un compte existe déjà avec cette adresse e-mail (via un autre moyen de connexion).",
        "auth/invalid-credential": "Les informations de connexion sont invalides ou ont expiré.",
        "auth/operation-not-allowed": "Cette méthode de connexion n’est pas activée.",
        "auth/requires-recent-login": "Veuillez vous reconnecter pour effectuer cette action.",
        "auth/provider-already-linked": "Ce fournisseur est déjà lié à ce compte.",
        "auth/no-such-provider": "Le fournisseur demandé n'est pas lié à ce compte.",

        // Popup & redirection
        "auth/popup-blocked": "La fenêtre d’authentification a été bloquée par le navigateur.",
        "auth/popup-closed-by-user": "La fenêtre d’authentification a été fermée avant la fin du processus.",
        "auth/unauthorized-domain": "Le domaine utilisé n’est pas autorisé pour l’authentification.",
        "auth/redirect-cancelled-by-user": "La redirection a été annulée par l'utilisateur.",

        // Actions par e-mail
        "auth/expired-action-code": "Ce lien a expiré.",
        "auth/invalid-action-code": "Ce lien est invalide ou a déjà été utilisé.",
        "auth/missing-email": "L'adresse e-mail est requise.",

        // Téléphone & multi-facteurs
        "auth/missing-phone-number": "Le numéro de téléphone est requis.",
        "auth/invalid-phone-number": "Le numéro de téléphone est invalide.",
        "auth/missing-verification-code": "Le code de vérification est requis.",
        "auth/invalid-verification-code": "Le code de vérification est invalide.",
        "auth/code-expired": "Le code de vérification a expiré.",
        "auth/missing-continue-uri": "Une URL de redirection est requise.",
        "auth/invalid-continue-uri": "L’URL de redirection est invalide.",
        "auth/missing-multi-factor-info": "Aucune information sur le second facteur n’a été fournie.",
        "auth/multi-factor-auth-required": "Une authentification avec second facteur est requise.",

        // Limites & sécurité
        "auth/too-many-requests": "Trop de tentatives échouées. Veuillez réessayer plus tard.",
        "auth/network-request-failed": "Une erreur réseau est survenue. Vérifiez votre connexion.",
        "auth/internal-error": "Une erreur interne est survenue. Veuillez réessayer.",

        // Par défaut
        "default": "Une erreur est survenue. Veuillez réessayer."
    };



    // Fonction utilitaire pour traduire les erreurs
    const getFirebaseErrorMessage = (error) => {
        return firebaseErrorMessages[error.code] || firebaseErrorMessages["default"];
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            alert("Le mot de passe doit contenir au moins 6 caractères.");
            return;
        }
        if (password !== confirmPassword) {
            alert("Les mots de passe ne correspondent pas.");
            return;
        }
        setIsRegisteringInProgress(true);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            await updateProfile(user, { displayName: prenom });

            const isAdmin = adminEmails.includes(email);

            await setDoc(doc(collection(db, "users"), user.uid), {
                email: user.email,
                prenom: prenom,
                nom: nom,
                tel: telephone,
                isAdmin: isAdmin
            });

            await sendEmailVerification(user);
            alert("Un e-mail de vérification a été envoyé. Veuillez vérifier votre boîte de réception (y compris les courriers indésirables). Une fois vérifié, vous pourrez vous reconnecter.");

            await signOut(auth);
            setIsRegistering(false);

            // Réinitialiser le formulaire
            setEmail("");
            setPassword("");
            setConfirmPassword("");
            setPrenom("");
            setNom("");
            setTelephone("");

        } catch (error) {
            alert(getFirebaseErrorMessage(error));
        } finally {
            setIsRegisteringInProgress(false);
        }
    };

    const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingInProgress(true);
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const userDoc = await getDoc(doc(collection(db, "users"), user.uid));
        const userData = userDoc.data();

        setUser({
            email: user.email,
            prenom: userData?.prenom || "Utilisateur",
            isAdmin: userData?.isAdmin || false,
        });

        setEmail("");
        setPassword("");
    } catch (error) {
        alert(getFirebaseErrorMessage(error));
    } finally {
        setIsLoggingInProgress(false); // ✅ toujours réactiver le bouton
    }
};

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            setUser(null);
        } catch (error) {
            alert(error.message);
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            alert("E-mail de réinitialisation envoyé.");
        } catch (error) {
            alert(getFirebaseErrorMessage(error));
        }
    };

    return (
        <>
            <div hidden={hidden} className="centered-container">
                {user ? (
                    <div>
                        <h2>Bonjour {user.prenom} !</h2>
                        {user.isAdmin && <p>Vous êtes l'administrateur</p>}
                        <button className="btn3" onClick={handleSignOut}>Se déconnecter</button>
                    </div>
                ) : (
                    <div>
                        {isRegistering ? (
                            <form className="test2-form" onSubmit={handleRegister}>
                                <h2>Inscription</h2>
                                <input
                                    type="text"
                                    className="input-field"
                                    placeholder="Prénom"
                                    value={prenom}
                                    onChange={(e) => setPrenom(e.target.value)}
                                    required
                                />
                                <input
                                    type="text"
                                    className="input-field"
                                    placeholder="Nom de famille"
                                    value={nom}
                                    onChange={(e) => setNom(e.target.value)}
                                    required
                                />
                                <div className="phone-input-container">
                                    <input
                                        type="tel"
                                        className="input-field"
                                        placeholder="Numéro de téléphone"
                                        value={telephone}
                                        onChange={(e) => setTelephone(e.target.value)}
                                        pattern="\d*" // Accepte uniquement les chiffres
                                        required
                                    />

                                    {/* Tooltip */}
                                    <div className="tooltip-text">
                                        Ce numéro de téléphone est uniquement utilisé pour des raisons de contact et ne sera pas utilisé à des fins commerciales.
                                    </div>
                                </div>
                                <input
                                    type="email"
                                    className="input-field"
                                    placeholder="Adresse mail"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                                <input
                                    type="password"
                                    className="input-field"
                                    placeholder="Mot de passe"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                                <input
                                    type="password"
                                    className="input-field"
                                    placeholder="Confirmer le mot de passe"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                />
                                <button className="btn" type="submit" disabled={isRegisteringInProgress}>
                                    S'inscrire
                                </button>
                                <button className="btn" type="button" onClick={() => setIsRegistering(false)}>Déjà un compte ? Se connecter</button>
                            </form>
                        ) : showResetPassword ? (
                            <form className="test2-form" onSubmit={handlePasswordReset}>
                                <h2>Mot de passe oublié ?</h2>
                                <input
                                    type="email"
                                    className="input-field"
                                    placeholder="Adresse mail"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    required
                                />
                                <button className="btn2" type="submit">Réinitialiser le mot de passe</button>
                                <button className="btn2" type="button" onClick={() => setShowResetPassword(false)}>Retour à la connexion</button>
                            </form>
                        ) : (
                            <form className="test2-form" onSubmit={handleLogin}>
                                <h2>Connexion</h2>
                                <input
                                    type="email"
                                    className="input-field"
                                    placeholder="Adresse mail"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                                <input
                                    type="password"
                                    className="input-field"
                                    placeholder="Mot de passe"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                                <button
                                    className="btn"
                                    type="submit"
                                    disabled={isLoggingInProgress}
                                >
                                    Se connecter
                                </button>
                                <button className="btn" type="button" onClick={() => setIsRegistering(true)}>Pas de compte ? S'inscrire</button>
                                <button className="btn" type="button" onClick={() => setShowResetPassword(true)}>Mot de passe oublié ?</button>
                            </form>
                        )}
                    </div>
                )}
            </div>
            <Footer setShowMentionsLegales={toggleFooter} />
        </>
    );
};

export default Compte;